import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  BriefDeliverableStatusEnum,
  BriefDeliverableTaskUploadTypeEnum,
} from "@entities/brief/lib/constants";
import useGenerateVideoCompliance from "@entities/brief/lib/hooks/darfting/useGenerateVideoCompliance";
import useGetMediaAssets from "@entities/brief/lib/hooks/darfting/useGetMediaAssets";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import {
  ComplianceCheckStatusReportEnum,
  IDraftingContents,
  IDraftingResponse,
} from "@shared/lib/types/drafting";
import { Button, Dropdown } from "@shared/ui";
import { Flex, MenuProps, Space } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  GitBranch,
  Loader2,
  RotateCw,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

interface DraftHeaderProps {
  versions?: IDraftingContents[];
  selectedVersionId?: string;
  onSelectVersion: (versionId: string) => void;
  status?: BriefDeliverableStatusEnum;
  title?: string;
  subtitle?: string;
  draft?: IDraftingResponse;
  handleUploadNewVersion: () => void;
  refetch: () => void;
  selectedAssetId?: string;
  onClose?: () => void;
}

export const DraftHeader = ({
  versions = [],
  selectedVersionId,
  onSelectVersion,
  status = BriefDeliverableStatusEnum.Pending,
  title = "Drafting",
  subtitle = "",
  draft,
  handleUploadNewVersion,
  refetch,
  selectedAssetId,
  onClose,
}: DraftHeaderProps) => {
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
  const [isComplianceOpen, setIsComplianceOpen] = useState(false);

  // Extract creator info
  const creatorName =
    draft?.campaignInfluencer?.influencerName ||
    `${draft?.campaignInfluencer?.influencer?.firstName || ""} ${draft?.campaignInfluencer?.influencer?.lastName || ""}`.trim() ||
    "";
  const creatorHandle = draft?.campaignInfluencer?.socialMedia?.username || "";
  const profilePicUrl =
    draft?.campaignInfluencer?.socialMedia?.profilePicUrl ||
    draft?.campaignInfluencer?.influencer?.profilePicUrl ||
    "";

  const complianceParams = useMemo(
    () => ({
      draftingId: draft?.drafting?._id || "",
      draftingVersionId: selectedVersionId || "",
      mediaAssetId: selectedAssetId || "",
    }),
    [draft?.drafting?._id, selectedAssetId, selectedVersionId]
  );

  const hasComplianceParams =
    !!complianceParams.draftingId &&
    !!complianceParams.draftingVersionId &&
    !!complianceParams.mediaAssetId;

  const {
    mediaAssetsInfo,
    isMediaAssetsFetching,
    isMediaAssetsFetched: mediaAssetsFetched,
    mediaAssetsRefetch,
  } = useGetMediaAssets(complianceParams);

  const {
    generateCompliance,
    isGeneratingCompliance,
    isGeneratingComplianceSuccess,
    generatedComplianceParams,
  } = useGenerateVideoCompliance();

  const { userDetails } = useGetMe();

  const complianceReport = mediaAssetsInfo?.data?.complianceCheckReport;
  const complianceCheckStatus = mediaAssetsInfo?.data?.complianceCheckStatus;
  const transcription = mediaAssetsInfo?.data?.transcription;
  const transcriptionStatus = mediaAssetsInfo?.data?.transcriptionStatus;

  const isTranscriptReady =
    transcriptionStatus === "Success" && (transcription?.length ?? 0) > 0;

  const complianceScore = complianceReport?.score ?? 0;

  const isAiPerformanceChecks = complianceReport?.isAiPerformanceChecks;
  const isAiBrandGuardRailsChecks = complianceReport?.isAiBrandGuardRailsChecks;
  const isAiAdvancedPromptingChecks =
    complianceReport?.isAiAdvancedPromptingChecks;
  const isAiConsistencyCheck = complianceReport?.isAiConsistencyCheck;
  const isAiSafetyChecks = complianceReport?.isAiSafetyChecks;
  const isAiScriptVsDraftingConsistencyCheck =
    complianceReport?.isAiScriptVsDraftingConsistencyCheck;

  const aiPerformanceChecks =
    complianceReport?.performanceGuidelinesChecks || [];
  const aiBrandGuardRailsChecks = complianceReport?.brandGuardRailsChecks || [];
  const aiAdvancedPromptingChecks =
    complianceReport?.advancedPromptingChecks || [];
  const aiConsistencyChecks = complianceReport?.consistencyChecks || [];
  const aiSafetyChecks = complianceReport?.safetyChecks || [];
  const aiScriptVsDraftingConsistencyChecks =
    complianceReport?.scriptVsDraftingConsistencyChecks || [];

  const allChecks = [
    ...(isAiPerformanceChecks ? aiPerformanceChecks : []),
    ...(isAiBrandGuardRailsChecks ? aiBrandGuardRailsChecks : []),
    ...(isAiAdvancedPromptingChecks ? aiAdvancedPromptingChecks : []),
    ...(isAiConsistencyCheck ? aiConsistencyChecks : []),
    ...(isAiSafetyChecks ? aiSafetyChecks : []),
    ...(isAiScriptVsDraftingConsistencyCheck
      ? aiScriptVsDraftingConsistencyChecks
      : []),
  ];
  const failCount = allChecks.filter(
    (check) => check.status === ComplianceCheckStatusReportEnum.NonCompliant
  ).length;
  const warningCount = allChecks.filter(
    (check) =>
      check.status === ComplianceCheckStatusReportEnum.PartiallyCompliant
  ).length;

  useEffect(() => {
    if (isGeneratingComplianceSuccess && mediaAssetsRefetch) {
      mediaAssetsRefetch();
    }
  }, [isGeneratingComplianceSuccess, mediaAssetsRefetch]);
  const isRunningAnalysis =
    complianceCheckStatus === "Pending" ||
    isGeneratingCompliance ||
    isMediaAssetsFetching;

  const complianceStatusConfig = useMemo(() => {
    if (isRunningAnalysis) {
      return {
        label: "Analyzing",
        tone: "pending" as const,
      };
    }

    switch (complianceReport?.status) {
      case ComplianceCheckStatusReportEnum.Compliant:
        return { label: "Compliant", tone: "success" as const };
      case ComplianceCheckStatusReportEnum.PartiallyCompliant:
        return { label: "Needs Attention", tone: "warning" as const };
      case ComplianceCheckStatusReportEnum.NonCompliant:
        return { label: "Non-Compliant", tone: "error" as const };
      default:
        return { label: "Compliance", tone: "neutral" as const };
    }
  }, [complianceCheckStatus, complianceReport?.status, isRunningAnalysis]);

  // Rolling compliance loading messages while analysis is running
  const complianceMessages = useMemo(
    () => [
      "AI signal scan in progress...",
      "Policy alignment underway...",
      "Guardrails recalibration...",
      "Checking compliance...",
      "Validating Accuracy...",
      "Analyzing Performance...",
      "Evaluating Engagement...",
      "Checking Brand Alignment...",
      "Analyzing Compliance...",
      "Validating Accuracy...",
      "Verifying Consistency...",
    ],
    []
  );
  const [complianceMessageIndex, setComplianceMessageIndex] = useState(0);

  useEffect(() => {
    if (!isRunningAnalysis) {
      setComplianceMessageIndex(0);
      return;
    }
    const id = setInterval(() => {
      setComplianceMessageIndex(
        (prev) => (prev + 1) % complianceMessages.length
      );
    }, 10000);
    return () => clearInterval(id);
  }, [isRunningAnalysis, complianceMessages.length]);

  const latestVersionId = draft?.drafting?.latestDraftingVersionId;
  const selectedVersion = versions?.find((v) => v._id === selectedVersionId);
  const versionNumber = selectedVersion?.versionNumber || 1;

  // Find the selected asset from the selected version
  const selectedAsset = useMemo(() => {
    if (!selectedVersion || !selectedAssetId) return null;
    return selectedVersion.mediaAssets?.find(
      (asset) => asset._id === selectedAssetId
    );
  }, [selectedVersion, selectedAssetId]);

  // Build version menu items for dropdown
  const versionMenuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [...(versions || [])]
      .reverse()
      .map((version) => {
        const isSelected = version._id === selectedVersionId;
        const isLatest = version._id === latestVersionId;
        const createdBy =
          `${version.createdBy?.firstName} ${version.createdBy?.lastName}` ||
          "System";

        const date = version.updatedAt
          ? dayjs(version.updatedAt).format("MMM DD, YYYY")
          : "";

        return {
          key: version._id,
          label: (
            <div
              className={classNames(styles.versionItem, {
                [styles.versionItemActive]: isSelected,
              })}
            >
              <div className={styles.versionMeta}>
                <div className={styles.versionTitleRow}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className={styles.versionTitle}>
                      Draft v{version.versionNumber}
                    </span>
                    {isLatest && (
                      <span className={styles.latestTag}>Latest</span>
                    )}
                  </div>
                  <span className={styles.versionDate}>{date}</span>
                </div>
                <div className={styles.versionAction}>
                  <span className={styles.versionUser}>
                    Created by {createdBy}
                  </span>
                </div>
              </div>
            </div>
          ),
        };
      });

    // Add divider and upload option if applicable
    if (userDetails?.userEntity.type === "admin-user") {
      if (
        (status === BriefDeliverableStatusEnum.EditsRequested ||
          status === BriefDeliverableStatusEnum.Pending) &&
        handleUploadNewVersion !== undefined
      ) {
        items.push({
          type: "divider",
          key: "divider",
        } as any);
        items.push({
          key: "upload",
          label: (
            <div className={styles.versionItem}>
              <div className={styles.versionMeta}>
                <div className={styles.versionTitleRow}>
                  <span className={styles.versionTitle}>
                    <PlusOutlined /> Upload New Version
                  </span>
                </div>
              </div>
            </div>
          ),
        });
      }
    }

    return items;
  }, [
    versions,
    selectedVersionId,
    latestVersionId,
    status,
    userDetails,
    handleUploadNewVersion,
  ]);

  const handleVersionMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "upload") {
      handleUploadNewVersion();
      setIsVersionDropdownOpen(false);
    } else {
      onSelectVersion(String(key));
      setIsVersionDropdownOpen(false);
    }
  };

  const handleDownloadContent = () => {
    if (!selectedAsset?.url) {
      console.error("No asset URL available for download");
      return;
    }

    // For YouTube links and media links, open in new tab
    if (
      selectedAsset.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink ||
      selectedAsset.type === BriefDeliverableTaskUploadTypeEnum.MediaLink
    ) {
      window.open(selectedAsset.url, "_blank");
    } else {
      // For images and videos, create a download link
      const link = document.createElement("a");
      link.href = selectedAsset.url;
      link.download = selectedAsset.name || `media-asset-${selectedAsset._id}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadTranscript = () => {
    if (!isTranscriptReady || !transcription?.length) return;

    const content = transcription
      .map((segment) => {
        const mins = Math.floor(segment.start / 60);
        const secs = Math.floor(segment.start % 60);
        const timeStr = `${mins}:${secs.toString().padStart(2, "0")}`;
        return `[${timeStr}] ${segment.text}`;
      })
      .join("\n");

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedAsset?.name || "draft"}-transcript.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadMenuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "content",
        label: (
          <Space size={8}>
            <Download size={14} style={{ marginTop: 4 }} />
            <span>Download Content</span>
          </Space>
        ),
        disabled: !selectedAsset?.url,
      },
      {
        key: "transcript",
        label: (
          <Space size={8}>
            <FileText size={14} style={{ marginTop: 4 }} />
            <span>Download Transcript</span>
          </Space>
        ),
        disabled: !isTranscriptReady,
      },
    ],
    [isTranscriptReady, selectedAsset?.url]
  );

  const handleDownloadMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "content") {
      handleDownloadContent();
      return;
    }

    if (key === "transcript") {
      handleDownloadTranscript();
    }
  };

  const statusBadge = useMemo(() => {
    switch (status) {
      case BriefDeliverableStatusEnum.Approved:
      case BriefDeliverableStatusEnum.Completed:
        return (
          <span className={`${styles.statusBadge} ${styles.statusApproved}`}>
            <CheckCircleOutlined />
            Approved
          </span>
        );
      case BriefDeliverableStatusEnum.EditsRequested:
        return (
          <span className={`${styles.statusBadge} ${styles.statusChanges}`}>
            <ExclamationCircleOutlined />
            Request Changes
          </span>
        );
      case BriefDeliverableStatusEnum.PendingApproval:
        return (
          <span className={`${styles.statusBadge} ${styles.statusReview}`}>
            <ClockCircleOutlined />
            Approval Pending
          </span>
        );
      case BriefDeliverableStatusEnum.Uploaded:
      case BriefDeliverableStatusEnum.Pending:
      default:
        return (
          <span className={`${styles.statusBadge} ${styles.statusDraft}`}>
            <ClockCircleOutlined />
            Draft
          </span>
        );
    }
  }, [status]);

  return (
    <div className={styles.header}>
      {/* Left: Profile Info */}
      <div className={styles.profileInfo}>
        <div className={styles.profileImage}>
          <img
            src={profilePicUrl || "/default-avatar.png"}
            alt={creatorName}
            className={styles.profileImg}
          />
        </div>
        <div>
          <h3 className={styles.headerTitle}>Draft Review</h3>
          <div className={styles.creatorInfo}>
            <span className={styles.creatorName}>{creatorName}</span>
            {creatorHandle && (
              <>
                <span className={styles.creatorSeparator}>•</span>
                <span className={styles.creatorHandle}>{creatorHandle}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right: Controls & Close */}
      <div className={styles.controls}>
        {/* Version Dropdown */}
        <Dropdown
          menu={{ items: versionMenuItems, onClick: handleVersionMenuClick }}
          trigger={["click"]}
          open={isVersionDropdownOpen}
          onOpenChange={setIsVersionDropdownOpen}
          placement="bottomRight"
          overlayClassName={styles.versionDropdownOverlay}
          dropdownRender={(menu) => (
            <div className={styles.versionDropdown}>
              <div className={styles.versionDropdownBody}>{menu}</div>
            </div>
          )}
        >
          <Button
            type="text"
            className={classNames(styles.versionButton, {
              [styles.versionButtonOpen]: isVersionDropdownOpen,
            })}
            icon={<GitBranch size={12} className={styles.versionIcon} />}
          >
            Draft v{versionNumber}
            <ChevronDown
              size={12}
              className={classNames(styles.versionChevron, {
                [styles.versionChevronOpen]: isVersionDropdownOpen,
              })}
            />
          </Button>
        </Dropdown>

        {/* status badge for admins */}

        {selectedVersionId === latestVersionId &&
          userDetails?.userEntity.type === "admin-user" && (
            <div className={styles.statusWrapper}>{statusBadge}</div>
          )}

        {/* AI Compliance Dropdown */}
        <Dropdown
          trigger={["click"]}
          open={isComplianceOpen}
          onOpenChange={setIsComplianceOpen}
          placement="bottomRight"
          overlayClassName={styles.aiDropdownOverlay}
          dropdownRender={() => (
            <div className={styles.aiDropdown}>
              <div className={styles.aiDropdownHeader}>
                <div className={styles.aiScoreWrap}>
                  <div className={styles.aiScoreLabel}>{complianceScore}%</div>
                  <div className={styles.aiScoreStatus}>
                    Compliance
                    <br />
                    Score
                  </div>
                </div>
                <div>
                  <span
                    className={classNames(styles.aiScoreStatusBadge, {
                      [styles.aiScoreStatusSuccess]:
                        complianceStatusConfig.tone === "success",
                      [styles.aiScoreStatusWarning]:
                        complianceStatusConfig.tone === "warning",
                      [styles.aiScoreStatusError]:
                        complianceStatusConfig.tone === "error",
                    })}
                  >
                    {complianceStatusConfig.label}
                  </span>
                </div>
              </div>

              <div className={styles.complianceDropdownBody}>
                {complianceReport && !isRunningAnalysis ? (
                  !(
                    complianceReport.isAiAdvancedPromptingChecks ||
                    complianceReport.isAiBrandGuardRailsChecks ||
                    complianceReport.isAiPerformanceChecks ||
                    complianceReport.isAiConsistencyCheck ||
                    complianceReport.isAiSafetyChecks ||
                    complianceReport.isAiScriptVsDraftingConsistencyCheck
                  ) ? (
                    <>
                      <div className={styles.complianceEmptyTitle}>
                        No compliance checks enabled
                      </div>
                      <div className={styles.complianceEmptySubtitle}>
                        Enable compliance checks to see flagged items.
                      </div>
                    </>
                  ) : aiBrandGuardRailsChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiAdvancedPromptingChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiPerformanceChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiConsistencyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiSafetyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    aiScriptVsDraftingConsistencyChecks.every(
                      (check) =>
                        check.status ===
                        ComplianceCheckStatusReportEnum.Compliant
                    ) &&
                    false ? (
                    <div className={styles.complianceAllClear}>
                      All checks passed
                    </div>
                  ) : (
                    <>
                      {isAiConsistencyCheck &&
                        renderGuidelineSection(
                          "Consistency Checks",

                          aiConsistencyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiScriptVsDraftingConsistencyCheck &&
                        renderGuidelineSection(
                          "Script vs Draft",

                          aiScriptVsDraftingConsistencyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiSafetyChecks &&
                        renderGuidelineSection(
                          "Safety & Guidelines",
                          aiSafetyChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiPerformanceChecks &&
                        renderGuidelineSection(
                          "Performance Checks",
                          aiPerformanceChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiBrandGuardRailsChecks &&
                        renderGuidelineSection(
                          "Brand Guardrails",

                          aiBrandGuardRailsChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                      {isAiSafetyChecks &&
                        renderGuidelineSection(
                          "Advanced Prompting",

                          aiAdvancedPromptingChecks.map((check) => ({
                            ...check,
                            status:
                              check.status as ComplianceCheckStatusReportEnum,
                          }))
                        )}
                    </>
                  )
                ) : (
                  <div className={styles.complianceEmpty}>
                    {isRunningAnalysis ? (
                      <Flex align="center" gap={8}>
                        <Loader2 size={14} className={styles.spin} />
                        <span>
                          {complianceMessages[complianceMessageIndex]}
                        </span>
                      </Flex>
                    ) : (
                      <>
                        <div className={styles.complianceEmptyTitle}>
                          No compliance report yet
                        </div>
                        <div className={styles.complianceEmptySubtitle}>
                          Run the compliance check to see flagged items.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.aiFooter}>
                <div className={styles.aiFooterLeft}>
                  {failCount > 0 && (
                    <div className={styles.aiFooterIndicator}>
                      <div className={styles.aiFooterDot} />
                      <span className={styles.aiFooterText}>
                        {failCount}{" "}
                        {failCount === 1 ? "review warning" : "review warnings"}
                      </span>
                    </div>
                  )}
                  {warningCount > 0 && (
                    <div className={styles.aiFooterIndicator}>
                      <div
                        className={classNames(
                          styles.aiFooterDot,
                          styles.aiFooterDotWarning
                        )}
                      />
                      <span className={styles.aiFooterText}>
                        {warningCount}{" "}
                        {warningCount === 1 ? "warning" : "warnings"}
                      </span>
                    </div>
                  )}
                </div>
                <Space size={2} align="center">
                  {/* <Button
                    type="default"
                    size="small"
                    onClick={() => {
                      window.open(ROUTES.AI_CONTROLS, "_blank");
                    }}
                  >
                    View AI control settings
                  </Button> */}
                  <button
                    onClick={() => {
                      if (!hasComplianceParams) return;
                      generateCompliance(complianceParams);
                    }}
                    disabled={!hasComplianceParams || isRunningAnalysis}
                    className={styles.aiRefreshButton}
                    title="Rerun Analysis"
                  >
                    {isRunningAnalysis ? (
                      <Loader2 size={14} className={styles.spin} />
                    ) : (
                      <RotateCw size={14} />
                    )}
                  </button>
                </Space>
              </div>
            </div>
          )}
        >
          <Button
            type="text"
            className={classNames(styles.aiButton, {
              [styles.aiButtonCompliant]:
                complianceStatusConfig.tone === "success",
              [styles.aiButtonWarning]:
                complianceStatusConfig.tone === "warning",
              [styles.aiButtonFail]: complianceStatusConfig.tone === "error",
              [styles.aiButtonPending]:
                complianceStatusConfig.tone === "pending",
              [styles.aiButtonNeutral]:
                complianceStatusConfig.tone === "neutral",
            })}
            icon={
              <Sparkles
                size={12}
                className={styles.aiIcon}
                fill="currentColor"
              />
            }
          >
            AI Review: {complianceStatusConfig.label}
            <ChevronDown
              size={12}
              className={classNames(styles.aiChevron, {
                [styles.aiChevronOpen]: isComplianceOpen,
              })}
            />
          </Button>
        </Dropdown>

        <div className={styles.divider} />

        <Dropdown
          menu={{ items: downloadMenuItems, onClick: handleDownloadMenuClick }}
          trigger={["click"]}
        >
          <button className={styles.closeButton} title="Download" type="button">
            <Download size={20} />
          </button>
        </Dropdown>

        {onClose && (
          <button
            onClick={onClose}
            className={styles.closeButton}
            title="Close"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

const renderGuidelineSection = (
  title: string,
  items: {
    point: string;
    status: ComplianceCheckStatusReportEnum;
    reason?: string;
  }[]
) => {
  if (items.length === 0) return null;
  return (
    <div key={`${title}`} className={styles.complianceCheckCard}>
      <div className={styles.checkCardHeader}>
        <span className={styles.checkCardTitle}>{title}</span>
      </div>
      <Flex vertical gap={8}>
        {items?.map((item, idx) => {
          const isPass =
            item.status === ComplianceCheckStatusReportEnum.Compliant;
          const isWarning =
            item.status === ComplianceCheckStatusReportEnum.PartiallyCompliant;
          const isError =
            item.status === ComplianceCheckStatusReportEnum.NonCompliant;

          return (
            <>
              {isPass ? (
                <>
                  {item.point && (
                    <div className={styles.checkPassHeader}>
                      <CheckCircle2
                        size={10}
                        className={styles.checkPassIcon}
                      />
                      <div className={styles.checkPassText}>{item.point}</div>
                    </div>
                  )}
                  {item.reason && (
                    <p className={styles.checkPassMessage}>{item.reason}</p>
                  )}
                </>
              ) : (
                <div className={styles.checkIssueBox}>
                  {item.point && (
                    <div className={styles.checkIssueHeader}>
                      <AlertTriangle
                        size={10}
                        className={styles.checkIssueIcon}
                      />
                      <div className={styles.checkIssueText}>{item.point}</div>
                    </div>
                  )}
                  {item.reason && (
                    <div className={styles.checkIssueReason}>{item.reason}</div>
                  )}
                </div>
              )}
            </>
          );
        })}
      </Flex>
    </div>
  );
};
